import { post } from "./aws_api_helper";
import * as url from "./url_helper";

const MIN_PART_BYTES = 5 * 1024 * 1024;
const DEFAULT_PART_BYTES = 25 * 1024 * 1024;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function unwrapInitBody(data) {
  if (!data || typeof data !== "object") return null;
  if (data.uploadId && data.key && Array.isArray(data.partUrls)) return data;
  if (data.upload_id && data.key && Array.isArray(data.part_urls)) {
    return {
      uploadId: data.upload_id,
      key: data.key,
      partSizeBytes: data.part_size_bytes ?? data.partSizeBytes,
      totalParts: data.total_parts ?? data.totalParts,
      partUrls: data.part_urls,
    };
  }
  const inner = data.data;
  if (inner && typeof inner === "object") {
    if (inner.uploadId && inner.key && Array.isArray(inner.partUrls)) return inner;
    if (inner.upload_id && inner.key && Array.isArray(inner.part_urls)) {
      return {
        uploadId: inner.upload_id,
        key: inner.key,
        partSizeBytes: inner.part_size_bytes ?? inner.partSizeBytes,
        totalParts: inner.total_parts ?? inner.totalParts,
        partUrls: inner.part_urls,
      };
    }
  }
  return null;
}

function normalizePartEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const partNumber = Number(entry.partNumber ?? entry.part_number);
  const partUrl =
    entry.url ?? entry.presignedUrl ?? entry.presigned_url ?? entry.signedUrl ?? entry.signed_url;
  if (!partNumber || !partUrl) return null;
  return { partNumber, url: partUrl };
}

/**
 * Normalizes the ETag from an UploadPart response for CompleteMultipartUpload.
 * Browsers return a quoted digest; some gateways double-wrap. API JSON usually expects
 * the same form as S3: a string containing quotes around the digest, e.g. "\"abc123\"".
 */
function etagForCompleteRequest(rawFromHeader) {
  let s = String(rawFromHeader ?? "").trim();
  if (!s) return s;
  if (s.startsWith("W/")) return s;
  let inner = s;
  while (inner.length >= 2 && inner.startsWith('"') && inner.endsWith('"')) {
    inner = inner.slice(1, -1).trim();
  }
  return `"${inner}"`;
}

/** Shown in UI when fetch() fails cross-origin (typical: S3 CORS missing/wrong origin). */
const CORS_HINT_UI =
  " S3 blocked this in the browser (CORS). The bucket rule must list your exact app origin (scheme + host + port), allow PUT, AllowedHeaders \"*\", and ExposeHeaders including ETag. Note: http://localhost:3000 and http://127.0.0.1:3000 are different origins.";

/**
 * PUT a single multipart part to S3. Uses ArrayBuffer (not Blob) so the browser does not
 * send Content-Type from File/Blob.type; presigned URLs often sign only `host`, and extra
 * headers can cause 403s. Bucket CORS must still allow PUT from your SPA origin.
 */
async function putS3Part(partUrl, fileSlice, { maxAttempts = 4 } = {}) {
  let lastErr = "Part upload failed.";
  const body = await fileSlice.arrayBuffer();

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const r = await fetch(partUrl, {
        method: "PUT",
        body,
        mode: "cors",
        cache: "no-store",
        credentials: "omit",
      });
      if (!r.ok) {
        lastErr = `S3 upload failed (${r.status}).`;
        await sleep(250 * attempt);
        continue;
      }
      const etag =
        r.headers.get("etag") || r.headers.get("ETag") || r.headers.get("Etag");
      if (!etag) {
        lastErr =
          "S3 response missing ETag. Add ExposeHeader for ETag (and often x-amz-request-id) on the bucket CORS rule.";
        await sleep(250 * attempt);
        continue;
      }
      return { ok: true, eTag: etag.trim() };
    } catch (e) {
      const msg = e?.message || "Network error during part upload.";
      const looksCors = /Failed to fetch|NetworkError|Load failed|CORS/i.test(msg);
      if (looksCors && typeof window !== "undefined" && attempt === 1) {
        console.warn(
          "[Offline CUR] Browser could not complete PUT to S3 (often CORS). " +
            `This app is loaded from origin: ${window.location.origin}. ` +
            "That exact string must appear under AllowedOrigins on the S3 bucket used for offline CUR. " +
            "Also set AllowedMethods (PUT), AllowedHeaders (*), ExposeHeaders (ETag). " +
            "In DevTools → Network, inspect the failing request to s3.amazonaws.com (OPTIONS or PUT)."
        );
      }
      lastErr = looksCors ? `${msg}${CORS_HINT_UI}` : msg;
      await sleep(250 * attempt);
    }
  }
  return { ok: false, error: lastErr };
}

/**
 * Offline CUR: init multipart upload → PUT each part to pre-signed URLs → complete (or abort on failure/cancel).
 * Backend calls use the shared axios client (auth + org scope). S3 uses raw fetch on the full pre-signed URL.
 *
 * UI (UploadOfflineCurModal): while init + before the first `onProgress`, show **Preparing**; then show part
 * progress. The secondary action during upload is labeled **Cancel upload** (stops the flow / aborts multipart).
 */
export async function uploadOfflineCurMultipartFile(
  file,
  { accountNumber, year, month },
  { onProgress, shouldAbort } = {}
) {
  let uploadId = null;
  let key = null;

  const callAbort = async () => {
    if (!uploadId || !key) return;
    try {
      await post(url.POST_OFFLINE_CUR_UPLOAD_ABORT, { uploadId, key });
    } catch (e) {
      /* non-fatal */
    }
  };

  try {
    if (shouldAbort?.()) {
      return { status: 499, data: { message: "Upload cancelled." } };
    }

    const partSizeBytes = Math.max(DEFAULT_PART_BYTES, MIN_PART_BYTES);
    const initPayload = {
      accountNumber: String(accountNumber).trim(),
      year: String(year),
      month: String(month).padStart(2, "0"),
      fileName: file.name,
      fileSizeBytes: file.size,
      partSizeBytes,
    };

    const initRes = await post(url.POST_OFFLINE_CUR_UPLOAD_INIT, initPayload);
    if (!(initRes?.status >= 200 && initRes?.status < 300)) {
      return initRes;
    }

    const body = unwrapInitBody(initRes.data);
    if (!body) {
      return {
        status: 502,
        data: { message: "Upload init response was missing uploadId, key, or partUrls." },
      };
    }

    uploadId = body.uploadId;
    key = body.key;
    const size =
      Number(body.partSizeBytes ?? body.part_size_bytes) > 0
        ? Number(body.partSizeBytes ?? body.part_size_bytes)
        : partSizeBytes;

    const rawParts = Array.isArray(body.partUrls) ? body.partUrls : [];
    const sorted = rawParts
      .map(normalizePartEntry)
      .filter(Boolean)
      .sort((a, b) => a.partNumber - b.partNumber);

    if (sorted.length === 0) {
      await callAbort();
      return {
        status: 502,
        data: { message: "No part URLs returned from upload init." },
      };
    }

    const uploadedParts = [];
    let offset = 0;

    for (let i = 0; i < sorted.length; i += 1) {
      if (shouldAbort?.()) {
        await callAbort();
        uploadId = null;
        key = null;
        return { status: 499, data: { message: "Upload cancelled." } };
      }

      const { partNumber, url: signedUrl } = sorted[i];
      const end = Math.min(offset + size, file.size);
      const chunk = file.slice(offset, end);
      offset = end;

      const put = await putS3Part(signedUrl, chunk);
      if (!put.ok) {
        await callAbort();
        uploadId = null;
        key = null;
        return { status: 502, data: { message: put.error } };
      }

      uploadedParts.push({
        partNumber,
        eTag: etagForCompleteRequest(put.eTag),
      });
      onProgress?.({ completed: i + 1, total: sorted.length });
    }

    if (shouldAbort?.()) {
      await callAbort();
      uploadId = null;
      key = null;
      return { status: 499, data: { message: "Upload cancelled." } };
    }

    const partsOrdered = [...uploadedParts].sort((a, b) => a.partNumber - b.partNumber);
    const completePayload = {
      uploadId,
      key,
      parts: partsOrdered.map((p) => ({
        partNumber: Number(p.partNumber),
        eTag: p.eTag,
      })),
    };

    const completeRes = await post(url.POST_OFFLINE_CUR_UPLOAD_COMPLETE, completePayload);

    if (!(completeRes?.status >= 200 && completeRes?.status < 300)) {
      await callAbort();
      uploadId = null;
      key = null;
      return completeRes;
    }

    uploadId = null;
    key = null;
    return completeRes;
  } catch (e) {
    await callAbort();
    uploadId = null;
    key = null;
    return {
      status: 500,
      data: { message: e?.message || "Upload failed." },
    };
  }
}
